import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import styles from './Shell.module.css';

// The design's toast: a short confirmation at the bottom (above the phone
// tabs) that fades after a few seconds. It survives a page change, so a
// screen can navigate and still say what happened.
type Toast = { show: (text: string) => void };

const ToastContext = createContext<Toast>({ show: () => undefined });
const SHOW_MS = 2600;
const LONG_SHOW_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState('');
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((next: string) => {
    setText(next);
    window.clearTimeout(timer.current);
    // Longer messages stay a little longer so they can be read.
    timer.current = window.setTimeout(() => setText(''), next.length > 60 ? LONG_SHOW_MS : SHOW_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.toastSlot} role="status" aria-live="polite">
        {text ? <div className={styles.toast}>{text}</div> : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Toast {
  return useContext(ToastContext);
}
